import { useState, useEffect } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { authService } from '../services/auth'
import MarketingHeader from '../components/MarketingHeader'
import './VerifyEmailPage.scss'

const VerifyEmailPage = () => {
  const { t } = useTranslation(['auth', 'common'])
  const [searchParams] = useSearchParams()

  const token = searchParams.get('token')
  const email = searchParams.get('email')

  // UI state
  const [verifying, setVerifying] = useState(false)
  const [verified, setVerified] = useState(false)
  const [error, setError] = useState('')
  const [errorCode, setErrorCode] = useState(null)
  const [resendLoading, setResendLoading] = useState(false)
  const [resendSuccess, setResendSuccess] = useState('')

  useEffect(() => {
    if (!token || !email) return

    const verify = async () => {
      setVerifying(true)
      setError('')
      setErrorCode(null)

      try {
        await authService.verifyEmail(token, email)
        setVerified(true)
      } catch (err) {
        const code = err.response?.data?.error || err.response?.data?.code
        setErrorCode(code)

        if (code === 'TOKEN_EXPIRED') {
          setError(t('auth:verifyEmail.linkExpired'))
        } else {
          setError(err.response?.data?.message || t('auth:verifyEmail.verificationFailed'))
        }
      } finally {
        setVerifying(false)
      }
    }

    verify()
  }, [token, email, t])

  const handleResendVerification = async () => {
    setResendLoading(true)
    setResendSuccess('')

    try {
      await authService.resendVerification(email)
      setResendSuccess(t('auth:verifyEmail.verificationResent'))
    } catch (err) {
      setError(err.response?.data?.message || t('auth:verifyEmail.resendFailed'))
    } finally {
      setResendLoading(false)
    }
  }

  // No token or email in URL
  if (!token || !email) {
    return (
      <div className="verify-email-page">
        <MarketingHeader />
        <div className="verify-email-page__card">
          <h1 className="verify-email-page__title">{t('auth:verifyEmail.title')}</h1>
          <div className="verify-email-page__message verify-email-page__message--error">
            <p>{t('auth:verifyEmail.invalidLink')}</p>
          </div>
          <div className="verify-email-page__links">
            <Link to="/login">{t('auth:verifyEmail.goToLogin')}</Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="verify-email-page">
      <MarketingHeader />
      <div className="verify-email-page__card">
        <h1 className="verify-email-page__title">{t('auth:verifyEmail.title')}</h1>

        {/* Loading state */}
        {verifying && (
          <div className="verify-email-page__message verify-email-page__message--loading">
            <p>{t('auth:verifyEmail.verifying')}</p>
          </div>
        )}

        {/* Success state */}
        {verified && (
          <>
            <div className="verify-email-page__message verify-email-page__message--success">
              <p>{t('auth:verifyEmail.successMessage')}</p>
            </div>
            <Link to="/login" className="verify-email-page__button verify-email-page__button--primary">
              {t('auth:verifyEmail.signIn')}
            </Link>
          </>
        )}

        {/* Error state */}
        {!verifying && error && (
          <>
            <div className="verify-email-page__message verify-email-page__message--error">
              <p>{error}</p>
            </div>

            {resendSuccess && (
              <div className="verify-email-page__success">
                {resendSuccess}
              </div>
            )}

            {errorCode === 'TOKEN_EXPIRED' && (
              <button
                type="button"
                className="verify-email-page__button verify-email-page__button--secondary"
                onClick={handleResendVerification}
                disabled={resendLoading}
              >
                {resendLoading ? t('auth:verifyEmail.resending') : t('auth:verifyEmail.resendVerification')}
              </button>
            )}

            <div className="verify-email-page__links">
              <Link to="/login">{t('auth:verifyEmail.goToLogin')}</Link>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default VerifyEmailPage
